"use client";

import { ArrowRight, Flower2, FlaskConical, Flame, Hourglass } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Logic() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    numeral: "01",
    title: "Diagnostic Mapping",
    description: "Lumina ingests student responses, mapping every step against established mark-scheme criteria to identify precise points of failure.",
    Icon: Flower2
  }, {
    numeral: "02",
    title: "Process Analysis",
    description: "We isolate the exact moment a method is misapplied, distinguishing between conceptual gaps and simple procedural errors.",
    Icon: Hourglass
  }, {
    numeral: "03",
    title: "Failure Identification",
    description: "The engine highlights where logic breaks down, providing clear, actionable data on why marks were lost during the assessment.",
    Icon: Flame
  }, {
    numeral: "04",
    title: "Targeted Feedback",
    description: "Educators receive granular insights, allowing for precise interventions that address the specific process errors identified.",
    Icon: FlaskConical
  }];
  return <section data-section-id="3177" id="mark-scheme-logic" className="relative overflow-hidden bg-background text-foreground py-20 md:py-32 px-6 md:px-12 lg:px-20">
      {}
      <svg aria-hidden="true" className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 h-[120%] w-auto opacity-15 hidden lg:block" viewBox="0 0 120 600" fill="none" stroke="currentColor" strokeWidth="0.8">
        <path d="M60 0 L60 600" />
        <g>
          {Array.from({
          length: 14
        }).map((_, i) => {
          const y = 40 + i * 40;
          const side = i % 2 === 0 ? 1 : -1;
          return <g key={i} data-index={i}>
                <path d={`M60 ${y} Q ${60 + side * 30} ${y - 10}, ${60 + side * 55} ${y - 5}`} />
                <ellipse cx={60 + side * 55} cy={y - 5} rx="14" ry="5" transform={`rotate(${side * 25}, ${60 + side * 55}, ${y - 5})`} fill="currentColor" fillOpacity="0.3" />
                <circle cx={60 + side * 20} cy={y + 4} r="2" fill="currentColor" fillOpacity="0.4" />
              </g>;
        })}
        </g>
      </svg>

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {}
        <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="font-mono text-sm tracking-widest text-muted-foreground mb-3">
            04 —
          </div>
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-primary mb-8">
            The Engine
          </div>

          <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-foreground mb-12">
            Mapping the
            <br />
            <em className="italic font-light">Mark-Scheme</em>
          </h2>

          <div className="space-y-0">
            {steps.map((step, i) => {
            const Icon = step.Icon;
            return <div key={step.numeral} data-index={i} className="group py-6 border-t border-border last:border-b">
                  <div className="flex items-start gap-5">
                    <Icon className="size-5 mt-1.5 text-primary shrink-0" strokeWidth={1.25} />
                    <div className="flex-1">
                      <div className="flex items-baseline gap-3 mb-2">
                        <span className="font-mono text-xs tracking-widest text-muted-foreground">
                          {step.numeral}.
                        </span>
                        <h3 className="font-default text-xl md:text-2xl text-foreground">
                          {step.title}
                        </h3>
                      </div>
                      <p className="font-default text-[15px] leading-relaxed text-muted-foreground max-w-md">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>

          <div className="mt-10">
            <Button asChild variant="outline" size="lg" className="bg-transparent border border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-lg px-7 py-6 font-mono text-xs tracking-[0.25em] uppercase shadow-md">
              <Link to="/PilotPage">
                Start a Conversation
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div ref={rightRef} className={`lg:col-span-7 relative transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="relative">
            {}
            <div className="relative w-full lg:w-[85%] ml-auto shadow-lg">
              <AspectRatio ratio={4 / 5}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/diagnostic_engine_interface.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/diagnostic_engine_interface.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/diagnostic_engine_interface.png-wvc-sizes" alt="Lumina diagnostic engine interface showing mark-scheme mapping" loading="lazy" decoding="async" className="size-full object-cover rounded-lg" />
              </AspectRatio>

              {}
              <div className="absolute -right-2 top-8 md:-right-6 md:top-12 bg-card border border-border px-4 py-3 shadow-md rotate-2 max-w-[220px] rounded-lg">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1">
                  Insight
                </div>
                <div className="font-default italic text-sm text-foreground leading-snug">
                  Identifying process errors at scale.
                </div>
              </div>
            </div>

            {}
            <div className="absolute left-0 -bottom-10 md:-bottom-16 lg:-left-12 w-[55%] md:w-[45%] lg:w-[42%] shadow-lg border-4 md:border-8 border-background rounded-lg">
              <AspectRatio ratio={3 / 4}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/student_assessment_data.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/student_assessment_data.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/student_assessment_data.webp-wvc-sizes" alt="Data visualization of student assessment performance" loading="lazy" decoding="async" className="size-full object-cover rounded-lg" />
              </AspectRatio>
            </div>
          </div>

          {}
          <div className="mt-24 md:mt-28 lg:mt-32 max-w-xs ml-auto pr-4 hidden md:block">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
              From Data to Clarity
            </div>
            <p className="font-default italic text-base text-muted-foreground leading-relaxed">
              Lumina transforms raw assessment data into actionable pedagogical insights.
            </p>
          </div>
        </div>
      </div>
    </section>;
}